import type { LeoAuthoringJob } from "./leo-jobs"
import type { QuestionQualityCheck } from "./types"

/** Named options that often land in finding copy as themselves. */
const NAMED_DISTRACTORS = ["All of the above", "None of the above"]

export type EmphasizedCopyPart = {
  text: string
  emphasize: boolean
}

/**
 * Phrases to mark in a finding title: the named options the sentence is about.
 * Longer first so "None of the above" wins over a shorter overlap.
 */
export function findingTitleEmphasis(
  check: Pick<QuestionQualityCheck, "title" | "markText" | "currentText">,
): string[] {
  const found: string[] = []
  const title = check.title
  const candidates = [
    ...NAMED_DISTRACTORS,
    check.markText?.trim() ?? "",
    check.currentText?.trim() ?? "",
  ].filter(Boolean)
  for (const phrase of candidates) {
    if (title.toLowerCase().includes(phrase.toLowerCase())) found.push(phrase)
  }
  return [...new Set(found)].sort((a, b) => b.length - a.length)
}

/** Phrases to mark in Leo's jobs note, e.g. "2 All and None in distractors." */
export function leoJobsNoteEmphasis(jobs: LeoAuthoringJob[]): string[] {
  const phrases = [
    "All and None",
    "distractors",
    "stem",
    ...NAMED_DISTRACTORS,
  ]
  for (const job of jobs) {
    switch (job.id) {
      case "options":
      case "variation-options":
        phrases.push("distractors")
        break
      case "stem":
      case "variation-stem":
        phrases.push("stem")
        break
      case "rationale":
        phrases.push("rationale")
        break
      case "classification":
        phrases.push("classification")
        break
    }
  }
  return [...new Set(phrases)].sort((a, b) => b.length - a.length)
}

export function splitEmphasizedCopy(
  copy: string,
  phrases: string[],
): EmphasizedCopyPart[] {
  if (!copy) return []
  const needles = [...new Set(phrases.filter(Boolean))].sort(
    (a, b) => b.length - a.length,
  )
  if (needles.length === 0) return [{ text: copy, emphasize: false }]

  const lower = copy.toLowerCase()
  type Hit = { start: number; end: number }
  const hits: Hit[] = []
  for (const phrase of needles) {
    const needle = phrase.toLowerCase()
    let from = 0
    while (from <= lower.length - needle.length) {
      const at = lower.indexOf(needle, from)
      if (at < 0) break
      const end = at + needle.length
      const overlaps = hits.some((hit) => at < hit.end && end > hit.start)
      if (!overlaps) hits.push({ start: at, end })
      from = at + needle.length
    }
  }
  hits.sort((a, b) => a.start - b.start)

  const parts: EmphasizedCopyPart[] = []
  let cursor = 0
  for (const hit of hits) {
    if (hit.start > cursor) {
      parts.push({ text: copy.slice(cursor, hit.start), emphasize: false })
    }
    parts.push({ text: copy.slice(hit.start, hit.end), emphasize: true })
    cursor = hit.end
  }
  if (cursor < copy.length) {
    parts.push({ text: copy.slice(cursor), emphasize: false })
  }
  return parts
}
